import React, { useEffect, useState, useRef } from "react";
import { FaShieldAlt, FaUserSecret, FaCloud, FaExclamationTriangle, FaSearch, FaClipboardCheck, FaUsers } from "react-icons/fa";

export default function Home({ onEstimate }) {
  const words = [
    "Cybersecurity Experts",
    "Penetration Testing",
    "Cloud Security",
    "Risk Assessment",
    "Digital Forensics",
    "Compliance & Data Protection",
    "Your Trusted Security Partner"
  ];
  const [idx, setIdx] = useState(0);
  const [text, setText] = useState("");
  const [del, setDel] = useState(false);

  // Typewriter effect
  useEffect(() => {
    const current = words[idx % words.length];
    let t;
    if (!del) {
      t = setTimeout(() => {
        setText(current.substring(0, text.length + 1));
        if (text.length + 1 === current.length) setTimeout(() => setDel(true), 800);
      }, 120);
    } else {
      t = setTimeout(() => {
        setText(current.substring(0, text.length - 1));
        if (text.length - 1 === 0) {
          setDel(false);
          setIdx(i => i + 1);
        }
      }, 70);
    }
    return () => clearTimeout(t);
  }, [text, del, idx]);

  // Commitment numbers animation
  const [counts, setCounts] = useState({ counties: 0, partners: 0, clients: 0 });
  const commitmentRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          animateCounts();
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.5 }
    );
    if (commitmentRef.current) observer.observe(commitmentRef.current);
  }, []);

  const animateCounts = () => {
    const duration = 2000;
    const frameRate = 30;
    const steps = duration / (1000 / frameRate);
    const targets = { counties: 3, partners: 5, clients: 10 };
    let frame = 0;
    const counter = setInterval(() => {
      frame++;
      setCounts({
        counties: Math.floor((targets.counties / steps) * frame),
        partners: Math.floor((targets.partners / steps) * frame),
        clients: Math.floor((targets.clients / steps) * frame)
      });
      if (frame >= steps) clearInterval(counter);
    }, 1000 / frameRate);
  };

  // Clients logos
  const clientLogos = [
    { logo: "/assets/client1.png", review: "Reliable and professional." },
    { logo: "/assets/client2.png", review: "Excellent cybersecurity team." },
    { logo: "/assets/client3.png", review: "Quick and thorough." },
    { logo: "/assets/client4.png", review: "We trust LongRange because they speak solutions, not jargon." },
    { logo: "/assets/client5.png", review: "Highly recommended." }
  ];
  const clientTrackRef = useRef();

  // Auto-scroll clients
  useEffect(() => {
    const track = clientTrackRef.current;
    let scrollAmount = 0;
    const speed = 1;
    const scroll = () => {
      scrollAmount += speed;
      if (scrollAmount >= track.scrollWidth / 2) scrollAmount = 0;
      track.scrollTo({ left: scrollAmount, behavior: "smooth" });
      requestAnimationFrame(scroll);
    };
    requestAnimationFrame(scroll);
  }, []);

  // Fade-in sections
  useEffect(() => {
    const sections = document.querySelectorAll(".fade-in-section");
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) entry.target.classList.add("visible");
        });
      },
      { threshold: 0.2 }
    );
    sections.forEach(sec => observer.observe(sec));
  }, []);

  // Back to top
  const [showTop, setShowTop] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setShowTop(window.scrollY > 300);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <>
      {/* Hero */}
      <section id="home" className="hero fade-in-section">
        <div className="container">
          <h1>&gt;∞ Greater Than Infinity</h1>
          <h2 style={{ marginTop: 8 }}>
            LongRange <span style={{ color: "#ff6600" }}>{text}</span>
          </h2>
          <p>We secure digital lives and businesses with relentless precision and local awareness.</p>
          <div style={{ marginTop: 20 }}>
            <button className="cta" onClick={onEstimate}>Request Estimate</button>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="services-section fade-in-section">
        <h2>Our Services</h2>
        <div className="services-list">
          <div className="service-item"><FaShieldAlt size={36} /><span>Cybersecurity Audits</span></div>
          <div className="service-item"><FaUserSecret size={36} /><span>Penetration Testing</span></div>
          <div className="service-item"><FaCloud size={36} /><span>Cloud Security</span></div>
          <div className="service-item"><FaExclamationTriangle size={36} /><span>Risk Assessment</span></div>
          <div className="service-item"><FaSearch size={36} /><span>Digital Forensics</span></div>
          <div className="service-item"><FaClipboardCheck size={36} /><span>Compliance & Data Protection</span></div>
          <div className="service-item"><FaUsers size={36} /><span>Security Awareness Training</span></div>
        </div>
        <a href="https://notion.so" target="_blank" rel="noopener noreferrer" className="see-more-btn">See More</a>
      </section>

      {/* Commitment */}
      <section id="commitment" className="commitment-section fade-in-section" ref={commitmentRef}>
        <h2>Tracking Our Commitment</h2>
        <div className="commitment-container">
          <div className="commitment-stat">
            <span>{counts.counties}+</span>
            <p>Counties Secured</p>
          </div>
          <div className="commitment-stat">
            <span>{counts.partners}+</span>
            <p>Partnerships</p>
          </div>
          <div className="commitment-stat">
            <span>{counts.clients}+</span>
            <p>Clients Protected</p>
          </div>
        </div>
        <p>
          Security isn’t about promises, it’s about performance.
Every project we deliver strengthens Africa’s digital backbone.
        </p>
      </section>

      {/* Clients */}
      <section id="clients" className="clients-section fade-in-section">
        <h2>Happy Clients</h2>
        <div className="client-carousel">
          <div className="client-track" ref={clientTrackRef}>
            {[...clientLogos, ...clientLogos].map((c, i) => (
              <div key={i} className="client-square">
                <img src={c.logo} alt={`Client ${i}`} />
                <div className="client-hover">{c.review}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Blog */}
<section id="blog" className="blog-section fade-in-section">
  <h2>Blog / News</h2>
  <div className="blog-posts">
    <div className="blog-post">
      <h3>Kenya’s Next Cyber Battleground</h3>
      <p>How attacks evolve when awareness stays low and what every business should know.</p>
      <a href="#" target="_blank" rel="noopener noreferrer">Read More</a>
    </div>

    <div className="blog-post">
      <h3>Protecting Content Creators Online</h3>
      <p>Your password isn’t the problem your pattern is. Learn how to defend your brand.</p>
      <a href="#" target="_blank" rel="noopener noreferrer">Read More</a>
    </div>

    <div className="blog-post">
      <h3>The New Era of Digital Forensics in Africa</h3>
      <p>From evidence recovery to data integrity, the unseen heroes of justice in cyberspace.</p>
      <a href="#" target="_blank" rel="noopener noreferrer">Read More</a>
    </div>

    <div className="blog-post">
      <h3>Cyber Awareness in the Workplace</h3>
      <p>Training isn’t boring it’s the new frontline. We make employees your strongest firewall.</p>
      <a href="#" target="_blank" rel="noopener noreferrer">Read More</a>
    </div>
  </div>
</section>

      {/* Back to Top */}
      {showTop && <button id="backToTop" onClick={scrollTop}>↑ Top</button>}
    </>
  );
}
